import { isoDateIn, WeekStripConfigSchema, type WeekStripConfig } from '@hud/core';
import { registerWidget, type WidgetProps } from '../registry.js';
import { useHud } from '../context.js';
import { useNow } from '../hooks/useNow.js';
import './weekstrip.css';

/**
 * The current week, with today marked.
 *
 * Dates are computed in the configured zone rather than the device's, so the
 * highlighted day is the one *you* consider today — which differs from the
 * browser's answer whenever you're travelling or working past midnight.
 */
function WeekStrip({ config }: WidgetProps<WeekStripConfig>) {
  const now = useNow(60_000);
  const { config: vault } = useHud();
  const todayIso = isoDateIn(now, config.timezone ?? vault.timezone);
  const days = weekOf(todayIso, config.startOn);

  return (
    <div class="hud-week">
      {days.map((day) => {
        const isToday = day.iso === todayIso;
        const className = isToday ? 'hud-week__day hud-week__day--today' : 'hud-week__day';
        const content = (
          <>
            <span class="hud-week__name">{day.name}</span>
            <span class="hud-week__num">{day.dayOfMonth}</span>
          </>
        );

        return config.linkTo ? (
          <a class={className} key={day.iso} href={`#/${config.linkTo.replace('{date}', day.iso)}`}>
            {content}
          </a>
        ) : (
          <div class={className} key={day.iso}>
            {content}
          </div>
        );
      })}
    </div>
  );
}

interface Day {
  iso: string;
  name: string;
  dayOfMonth: number;
}

/**
 * The seven days surrounding `todayIso`.
 *
 * Built by stepping a UTC-noon Date so the arithmetic never lands on a DST
 * transition — adding 24h to local midnight skips or repeats a day twice a year.
 */
function weekOf(todayIso: string, startOn: 'monday' | 'sunday'): Day[] {
  const [year, month, day] = todayIso.split('-').map(Number) as [number, number, number];
  const anchor = new Date(Date.UTC(year, month - 1, day, 12));

  const weekday = anchor.getUTCDay(); // 0 = Sunday
  const offset = startOn === 'monday' ? (weekday + 6) % 7 : weekday;

  const start = new Date(anchor);
  start.setUTCDate(start.getUTCDate() - offset);

  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(start);
    date.setUTCDate(date.getUTCDate() + i);
    return {
      iso: date.toISOString().slice(0, 10),
      name: new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: 'UTC' }).format(date),
      dayOfMonth: date.getUTCDate(),
    };
  });
}

registerWidget({
  type: 'weekstrip',
  schema: WeekStripConfigSchema,
  component: WeekStrip,
});
